import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetCategories } from "~/api/dropshipping/queries";
import { IOnboardingChoices } from "~/types/account";
import * as S from "./styles";
import RadioButton from "../ui/Radio";
import CustomCheckbox from "../ui/Checkbox";
import Loader from "../ui/Loader";

type Props = {
  onboardingChoices: IOnboardingChoices | undefined;
  setOnboardingChoices: Dispatch<
    SetStateAction<IOnboardingChoices | undefined>
  >;
};

export const SelectCategory = ({
  onboardingChoices,
  setOnboardingChoices,
}: Props) => {
  const { t } = useTranslation();
  const { data: categories } = useGetCategories();
  const trendingCategory = { id: null, name: t("onboarding.trending") };
  const allOfAboveCategory = { id: "all", name: "All of above" };

  const orderedNames = [
    "Women's Clothing",
    "Tech Accessories",
    "Bath & Beauty",
    "Pets",
    "Accessories",
    "Men's Clothing",
  ];

  const filteredCategories =
  categories?.filter((category) => orderedNames.includes(category.name)) || [];

  const sortedCategories = orderedNames
    .map(name => filteredCategories.find(cat => cat.name === name))
    .filter(Boolean);

  const newCategories = [
    trendingCategory,
    ...sortedCategories,
    allOfAboveCategory,
  ];

  useEffect(() => {
    setOnboardingChoices((prev) => ({
      ...prev,
      selectedCategories: [null],
    }));
  }, []);

  const allIds = newCategories
    .filter(cat => cat && cat.id !== "all")
    .map(cat => cat ? cat.id : undefined)
    .filter((id): id is string | number | null => id !== undefined);

  const areAllCategoriesSelected = () =>
    onboardingChoices?.selectedCategories?.length === allIds.length &&
    allIds.every((id) => onboardingChoices?.selectedCategories?.includes(id));

  const handleAllSelection = () => {
    console.log("All of above clicked");
    if (areAllCategoriesSelected()) {
      setOnboardingChoices((prev) => ({
        ...prev,
        selectedCategories: [],
      }));
    } else {
      setOnboardingChoices((prev) => ({
        ...prev,
        selectedCategories: allIds,
      }));
    }
  };

  const handlePrevSelection = (
    prev: IOnboardingChoices | undefined,
    id: string | number
  ) => {
    if (prev?.selectedCategories?.includes(id)) {
      return prev?.selectedCategories?.filter((catId) => catId !== id);
    } else {
      // If 'null' is already selected, remove it before adding the new category
      const newCategories = prev?.selectedCategories?.includes(null)
        ? []
        : [...(prev?.selectedCategories || [])];
      return [...newCategories, id];
    }
  };

  // Function to handle individual category selection
  const handleCategorySelection = (id: string | number) => {
    setOnboardingChoices((prev) => ({
      ...prev,
      selectedCategories: handlePrevSelection(prev, id),
    }));
  };

  const handleCategorySelect = (id: string | number | null) => {
    if (id === null) {
      if (onboardingChoices?.selectedCategories?.includes(null)) {
        setOnboardingChoices((prev) => ({
          ...prev,
          selectedCategories: [],
        }));
      } else {
        setOnboardingChoices((prev) => ({
          ...prev,
          selectedCategories: [null],
        }));
      }
    } else if (id === "all") {
      handleAllSelection();
    } else {
      handleCategorySelection(id);
    }
  };

  return (
    <S.CategoryModalContent>
    <S.CardContainer>
      {newCategories.length > 0 ? (
        newCategories?.map((category) => {
          if (!category) return null;
          const isAllOfAbove = category.id === "all";
          return (
            <S.CardCategory
              key={category.id}
              className={
                category.id === "all"
                  ? areAllCategoriesSelected()
                    ? "active"
                    : ""
                  : onboardingChoices?.selectedCategories?.includes(category.id)
                    ? "active"
                    : ""
              }
              onClick={() => handleCategorySelect(category.id)} 
            >
              <>
                {isAllOfAbove ? (
                  <CustomCheckbox
                    checked={areAllCategoriesSelected()}
                    stopPropagation={true}
                    onChange={() => handleCategorySelect(category.id)}
                  />
                ) : category.id === null ? (
                  <RadioButton
                    checked={onboardingChoices?.selectedCategories?.includes(
                      category.id
                    )}
                    onChange={() => handleCategorySelect(category.id)}
                  />
                ) : (
                  <CustomCheckbox
                    onChange={() => handleCategorySelect(category.id)}
                    checked={onboardingChoices?.selectedCategories?.includes(
                      category.id
                    )}
                  />
                )}
              </>
              {category.name}
            </S.CardCategory>
          );
        })
      ) : (
        <S.LoaderContainer>
          <Loader fullWidth />
        </S.LoaderContainer>
      )}
    </S.CardContainer>
    </S.CategoryModalContent>
  );
};
